import { useState, useEffect, useRef } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { observer } from "mobx-react";
import Product from './Product';
import ProductViewDesktop from './ProductViewDesktop';
import ProductViewMobile from './ProductViewMobile';

const ProductView = observer(({
  product,
  productClasses,
  inCart,
  img,
  handleClick,
  selectedVariation,
  setSelectedVariation,
  productType,
  productDisplayType,
  setProductDisplayType,
  handleRemoveFromCart,
  handleAddToCart,
  handleClearFromCart,
  handleUpdateQuantity,
  productWeightQuantity,
  quantity,
  handleVariationChange,
  crosssellProduct,
  studioStore,
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isImageHovered, setIsImageHovered] = useState(false);
  const [isRTL, setIsRTL] = useState(false);
  const [offset, setOffset] = useState(0);
  const primaryColorRef = useRef();

  useEffect(() => {
    if (studioStore && studioStore.settings && studioStore.settings.colors) {
      primaryColorRef.current = studioStore.settings.colors.primary_color;
    }
  }, [studioStore]);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      // Detect RTL
      setIsRTL(document.documentElement.dir === 'rtl');

      // Calculate the 2rem offset in pixels and store it
      const remToPx = parseFloat(getComputedStyle(document.documentElement).fontSize) * 2;
      setOffset(remToPx);
    }
  }, []);

  const slideInVariants = {
    hidden: { x: isRTL ? '-100%' : '100%', opacity: 0 },
    visible: { x: isRTL ? `-${offset - 5}px` : `${offset - 5}px`, opacity: 1 },
    exit: { x: isRTL ? '-100%' : '100%', opacity: 0 },
  };

  return (
    <div
      className="product-wrapper relative z-10 mb-3 md:mb-0"
      style={{ backgroundColor: 'rgba(255, 255, 255, 0)' }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div
        data-testid={`product-card-${product.id}`}
        className={[
          ...productClasses,
        ].join(' ') + ` relative z-20 overflow-hidden transition-shadow duration-200 ${isHovered ? 'shadow-lg' : 'shadow-none'} ${selectedVariation ? 'variation-' + selectedVariation.id : 'product-' + product.id}`}
      >
        <ProductViewDesktop
          product={product}
          img={img}
          handleClick={handleClick}
          selectedVariation={selectedVariation}
          setSelectedVariation={setSelectedVariation}
          productType={productType}
          productDisplayType={productDisplayType}
          setProductDisplayType={setProductDisplayType}
          handleRemoveFromCart={handleRemoveFromCart}
          handleAddToCart={handleAddToCart}
          handleClearFromCart={handleClearFromCart}
          handleUpdateQuantity={handleUpdateQuantity}
          productWeightQuantity={productWeightQuantity}
          quantity={quantity}
          handleVariationChange={handleVariationChange}
          inCart={inCart}
          isImageHovered={isImageHovered}
          setIsImageHovered={setIsImageHovered}
          primaryColorRef={primaryColorRef}
        />

        <ProductViewMobile
          product={product}
          img={img}
          handleClick={handleClick}
          selectedVariation={selectedVariation}
          setSelectedVariation={setSelectedVariation}
          productType={productType}
          productDisplayType={productDisplayType}
          setProductDisplayType={setProductDisplayType}
          handleRemoveFromCart={handleRemoveFromCart}
          handleAddToCart={handleAddToCart}
          handleUpdateQuantity={handleUpdateQuantity}
          productWeightQuantity={productWeightQuantity}
          quantity={quantity}
          handleVariationChange={handleVariationChange}
          inCart={inCart}
          primaryColorRef={primaryColorRef}
        />
      </div>

      <AnimatePresence>
        {!!inCart && crosssellProduct && isHovered && (
          <motion.div
            key="crosssell"
            initial="hidden"
            animate="visible"
            exit="exit"
            variants={slideInVariants}
            transition={{ type: "tween", duration: 0.4 }}
            className="absolute top-0 right-full rtl:left-full rtl:right-auto w-[233px] h-full border border-yellow-400 rounded-md bg-white z-0"
          >
            <Product product={crosssellProduct} view="crosssell" />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
});

export default ProductView;
